import React, { useState, useEffect, useMemo, useRef } from 'react'
import { FuelDataRecord } from './DailyFuelData'
import { DailyMovementReportRecord } from '../core/_dmrModels'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface FuelRow {
  engine: string
  consumption: string
  lastRunningHours: string
  openingRunningHours: string
  closingRunningHours: string
  runningHours: string
  average: string
  isCustomEngine?: boolean
}

interface LubeRow {
  grades: string
  openingStock: string
  received: string
  consumedByVessel: string
  closingStock: string
  isCustomGrade?: boolean
}

const ENGINE_OPTIONS = ['M/E', 'Hyd engine', 'Dredge engine', 'A/E', 'Harbor D/G', 'B.T', 'Crane Engine'] as const

const LUBE_OIL_GRADE_OPTIONS = [
  'DISOLA M 4015/Gadinia s3 40/ MAK 401',
  '15W40',
  'SHELL CORENA S2P 100/ HYCOM C-100',
  'MOBIL SHC 629 / MAX SYNGEAR 150',
  'MAK AMOCAM 68/OMALA S2G 68/ MOBIL GEAR 600 XP-68',
  'AWHM-100',
] as const

const blankFuelRow = (): FuelRow => ({
  engine: '',
  consumption: '',
  lastRunningHours: '',
  openingRunningHours: '',
  closingRunningHours: '',
  runningHours: '',
  average: '',
})

const blankLubeRow = (): LubeRow => ({
  grades: '',
  openingStock: '',
  received: '',
  consumedByVessel: '',
  closingStock: '',
})

type Props = {
  onSave: (data: FuelDataRecord) => void
  selectedVessel: string
  reportDate: string
  previousReport?: FuelDataRecord | null
  currentDraft?: DailyMovementReportRecord | null
}

let sessionCustomEngines: string[] = []

const getSelectableEquipmentForRow = (
  rows: FuelRow[],
  rowIndex: number,
  options: readonly string[]
): string[] => {
  const currentValue = rows[rowIndex]?.engine ?? ''
  const selectedByOtherRows = new Set(
    rows
      .filter((_, idx) => idx !== rowIndex)
      .map((row) => row.engine)
      .filter((value) => value !== '' && value !== 'Others')
  )

  const availableOptions = [...options, ...sessionCustomEngines].filter((option) => !selectedByOtherRows.has(option))

  if (currentValue && currentValue !== 'Others' && !availableOptions.includes(currentValue)) {
    return [currentValue, ...availableOptions]
  }

  return availableOptions
}

const getSelectableGradesForRow = (
  rows: LubeRow[],
  rowIndex: number,
  options: readonly string[]
): string[] => {
  const currentValue = rows[rowIndex]?.grades ?? ''
  const selectedByOtherRows = new Set(
    rows
      .filter((_, idx) => idx !== rowIndex)
      .map((row) => row.grades)
      .filter((value) => value !== '' && value !== 'Others')
  )

  const availableOptions = options.filter((option) => !selectedByOtherRows.has(option))

  if (currentValue && currentValue !== 'Others' && !availableOptions.includes(currentValue)) {
    return [currentValue, ...availableOptions]
  }

  return availableOptions
}

const parseRunningHoursTotal = (report?: FuelDataRecord | null) => {
  if (!report) return ''

  const explicitTotal = report.totalRunningHours ?? (report as any).dfdTotalRunningHrs
  if (explicitTotal !== undefined && explicitTotal !== null && String(explicitTotal).trim() !== '') {
    return String(explicitTotal)
  }

  const total = (report.fuelRows || []).reduce((sum, row) => sum + (parseFloat(row.closingRunningHours) || 0), 0)
  return total > 0 ? total.toFixed(1) : '0.0'
}

const resolvePreviousClosingHours = (row: any) => {
  const getVal = (...keys: string[]) => {
    for (const k of keys) {
      if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') {
        return row[k]
      }
    }
    return undefined
  }

  const explicitClosing = getVal('closingRunningHours', 'closingRunningHrs', 'closingHours')
  if (explicitClosing !== undefined) {
    return String(explicitClosing)
  }

  const lastHrsStr = getVal('lastRunningHours', 'lastRunningHrs', 'previousRunningHours', 'previousRunningHrs')
  const lastHrs = lastHrsStr !== undefined ? parseFloat(String(lastHrsStr)) : 0

  const explicitOpening = getVal('openingRunningHours', 'openingRunningHrs', 'openingHours', 'runningHours', 'runningHrs')
  let openingHrs = explicitOpening !== undefined ? parseFloat(String(explicitOpening)) : NaN

  if (isNaN(openingHrs)) {
    const consumption = parseFloat(row.consumption)
    const average = parseFloat(row.average)
    if (!isNaN(consumption) && !isNaN(average) && average !== 0) {
      openingHrs = consumption / average
    } else {
      openingHrs = 0
    }
  }

  if (!isNaN(lastHrs) && !isNaN(openingHrs)) {
    const total = lastHrs + openingHrs
    return total > 0 ? total.toFixed(1) : '0'
  }

  return ''
}

/** Build a lookup of engine -> closingRunningHours from the previous report's fuelRows */
const buildPreviousClosingHoursMap = (report?: FuelDataRecord | null): Record<string, string> => {
  if (!report?.fuelRows) return {}
  const map: Record<string, string> = {}
  for (const row of report.fuelRows) {
    const engine = String(row.engine || '')
    if (engine) {
      const closing = resolvePreviousClosingHours(row)
      if (closing !== '') {
        map[engine] = closing
      }
    }
  }
  return map
}

const getPreviousOpeningStock = (report?: FuelDataRecord | null) => {
  if (!report) return ''

  const fallbackClosing = report.closingStock ?? (report as any).dfdClosingStock
  if (fallbackClosing !== undefined && fallbackClosing !== null && String(fallbackClosing).trim() !== '') {
    return String(fallbackClosing)
  }

  const explicitOpening = report.openingStock ?? (report as any).dfdOpeningStock
  if (explicitOpening !== undefined && explicitOpening !== null && String(explicitOpening).trim() !== '') {
    return String(explicitOpening)
  }

  return ''
}

const resolveLubeClosingStock = (row: any) => {
  const explicitClosing = row.closingStock ?? row.closing
  if (explicitClosing !== undefined && explicitClosing !== null && explicitClosing !== '' && explicitClosing !== '—') return explicitClosing

  const openingStock = parseFloat(row.openingStock)
  if (!isNaN(openingStock)) {
    const received = parseFloat(row.received) || 0
    const consumed = parseFloat(row.consumedByVessel) || 0
    return (openingStock + received - consumed).toFixed(3).replace(/\.?0+$/, '')
  }
  return ''
}

const AddDailyFuelDataModal: React.FC<Props> = ({ onSave, selectedVessel, reportDate, previousReport, currentDraft }) => {
  const [fuelRows, setFuelRows] = useState<FuelRow[]>([blankFuelRow()])
  const [lubeRows, setLubeRows] = useState<LubeRow[]>([blankLubeRow()])

  // Summary-level state
  const [openingStock, setOpeningStock] = useState('')
  const [bunker, setBunker] = useState('')
  const [lastRunningHours, setLastRunningHours] = useState('')
  const [remarks, setRemarks] = useState('')

  const hasPreviousReport = Boolean(previousReport)
  const hasPreviousRunningHours = Boolean(previousReport)

  const isDraftFuelEmpty =
    (currentDraft?.fuelRows?.length || 0) === 0 &&
    (currentDraft?.lubeRows?.length || 0) === 0 &&
    !currentDraft?.dfdOpeningStock &&
    !currentDraft?.dfdBunker &&
    !currentDraft?.dfdLastRunningHrs

  const initializedVessel = useRef<string | null>(null)

  useEffect(() => {
    if (initializedVessel.current === selectedVessel) {
      return
    }

    if (!isDraftFuelEmpty && currentDraft) {
      const mappedFuelRows = (currentDraft.fuelRows || []).map((row: any) => ({
        engine: String(row.engine ?? ''),
        consumption: String(row.consumption ?? ''),
        lastRunningHours: String(row.lastRunningHours ?? ''),
        openingRunningHours: String(row.openingRunningHours ?? ''),
        closingRunningHours: String(row.closingRunningHours ?? ''),
        runningHours: String(row.runningHours ?? ''),
        average: String(row.average ?? ''),
      }))

      const mappedLubeRows = ((currentDraft as any).lubeRows || []).map((row: any) => ({
        grades: String(row.grades ?? ''),
        openingStock: String(row.openingStock ?? ''),
        received: String(row.received ?? ''),
        consumedByVessel: String(row.consumedByVessel ?? ''),
        closingStock: String(row.closingStock ?? ''),
      }))

      setFuelRows(mappedFuelRows.length ? mappedFuelRows : [blankFuelRow()])
      setLubeRows(mappedLubeRows.length ? mappedLubeRows : [blankLubeRow()])
      setOpeningStock(currentDraft.dfdOpeningStock || '')
      setBunker(currentDraft.dfdBunker || '')
      setLastRunningHours(currentDraft.dfdLastRunningHrs || '')
      setRemarks(currentDraft.dfdRemarks || (currentDraft as any).remarks || '')
      initializedVessel.current = selectedVessel
      return
    }
    if (previousReport?.fuelRows && previousReport.fuelRows.length > 0) {
      const initRows = previousReport.fuelRows.map(pr => {
        const row = blankFuelRow()
        row.engine = String(pr.engine || '')
        row.lastRunningHours = resolvePreviousClosingHours(pr)
        
        // If engine is not empty, 'Others', or standard, it's custom
        if (row.engine && row.engine !== 'Others' && !['M/E', 'PORT M/E', 'STBD M/E', 'A/E', 'PORT A/E', 'STBD A/E', 'HARBOR D/G', 'HYD ENGINE', 'DREDGE ENGINE', 'B/T'].includes(row.engine.toUpperCase())) {
          row.isCustomEngine = true
        }
        return row
      })
      setFuelRows(initRows)
    } else {
      setFuelRows([blankFuelRow()])
    }
    
    if (previousReport?.lubeRows && previousReport.lubeRows.length > 0) {
      const initLubeRows = previousReport.lubeRows.map(pr => {
        const row = blankLubeRow()
        const resolvedClosing = resolveLubeClosingStock(pr) || ''
        row.grades = String(pr.grades || '')
        row.openingStock = String(resolvedClosing)
        
        if (row.grades && row.grades !== 'Others' && !(LUBE_OIL_GRADE_OPTIONS as readonly string[]).includes(row.grades)) {
          row.isCustomGrade = true
        }
        return row
      })
      setLubeRows(initLubeRows)
    } else {
      setLubeRows([blankLubeRow()])
    }

    setBunker('')
    setOpeningStock(getPreviousOpeningStock(previousReport))
    setLastRunningHours(parseRunningHoursTotal(previousReport))
    setRemarks('')
    initializedVessel.current = selectedVessel
  }, [previousReport, currentDraft, isDraftFuelEmpty, selectedVessel])

  // Auto-calculate total consumption from all rows
  const totalConsumption = useMemo(() => {
    const total = fuelRows.reduce((sum, row) => {
      return sum + (parseFloat(row.consumption) || 0)
    }, 0)
    return total
  }, [fuelRows])

  // Auto-calculate total running hours: Top Row Last Running Hrs + Sum of all current rows' Closing Running Hours
  const totalRunningHours = useMemo(() => {
    const totalClosing = fuelRows.reduce((sum, row) => {
      return sum + (parseFloat(row.closingRunningHours) || 0)
    }, 0)
    const topLastHrs = parseFloat(lastRunningHours) || 0
    return topLastHrs + totalClosing
  }, [fuelRows, lastRunningHours])

  // Auto-calculate closing stock: Opening Stock - Total Consumption + Bunker (minimum 0)
  const closingStockRaw = useMemo(() => {
    const opening = parseFloat(openingStock) || 0
    const bunkerVal = parseFloat(bunker) || 0
    const result = opening - totalConsumption + bunkerVal

    if (opening > 0 || bunkerVal > 0 || totalConsumption > 0) {
      return result
    }
    return null
  }, [openingStock, totalConsumption, bunker])

  const closingStock = useMemo(() => {
    if (closingStockRaw === null) return ''
    return Math.max(0, closingStockRaw).toFixed(3)
  }, [closingStockRaw])

  // The maximum total consumption allowed before closing stock hits 0
  const maxAllowedConsumption = useMemo(() => {
    const opening = parseFloat(openingStock) || 0
    const bunkerVal = parseFloat(bunker) || 0
    return Math.max(0, opening + bunkerVal)
  }, [openingStock, bunker])

  // Compute per-row max allowed consumption
  const getMaxConsumptionForRow = (rowIndex: number) => {
    const otherRowsConsumption = fuelRows.reduce((sum, row, idx) => {
      if (idx === rowIndex) return sum
      return sum + (parseFloat(row.consumption) || 0)
    }, 0)
    return Math.max(0, maxAllowedConsumption - otherRowsConsumption)
  }

  const handleFuelRowChange = (
    index: number,
    field: keyof FuelRow,
    value: string
  ) => {
    if (field !== 'engine' && field !== 'isCustomEngine' && value !== '' && !/^\d*\.?\d*$/.test(value)) {
      return
    }

    setFuelRows((prev) => {
      const copy = [...prev]
      const updatedRow = { ...copy[index], [field]: value }

      if (field === 'engine' && value === 'Others') {
        updatedRow.isCustomEngine = true
        updatedRow.engine = '' // clear to allow typing
      } else if (field === 'engine' && updatedRow.isCustomEngine) {
        // user typing custom name
        updatedRow.engine = value
      }

      // Auto-fill lastRunningHours from previous report when engine is selected
      if (field === 'engine' && value !== 'Others' && !updatedRow.isCustomEngine && previousReport) {
        const prevClosingMap = buildPreviousClosingHoursMap(previousReport)
        if (prevClosingMap[value]) {
          updatedRow.lastRunningHours = prevClosingMap[value]
        } else {
          updatedRow.lastRunningHours = ''
        }
      }

      // Auto-calculate Closing Running Hours and Average
      if (field === 'lastRunningHours' || field === 'openingRunningHours' || field === 'consumption') {
        const lastHrs = parseFloat(updatedRow.lastRunningHours) || 0
        const openingHrs = parseFloat(updatedRow.openingRunningHours) || 0
        const consumption = parseFloat(updatedRow.consumption) || 0
        
        const closing = lastHrs + openingHrs
        updatedRow.closingRunningHours = closing > 0 ? closing.toFixed(1) : ''

        if (openingHrs > 0) {
          updatedRow.average = (consumption / openingHrs).toFixed(3)
        } else if (consumption > 0) {
          updatedRow.average = '0.000'
        } else {
          updatedRow.average = ''
        }
      }

      copy[index] = updatedRow
      return copy
    })
  }

  const handleCustomEngineBlur = (index: number) => {
    const row = fuelRows[index]
    if (row.isCustomEngine && row.engine.trim()) {
      const customName = row.engine.trim()
      if (!sessionCustomEngines.includes(customName)) {
        sessionCustomEngines.push(customName)
      }
      setFuelRows((prev) => {
        const copy = [...prev]
        copy[index] = { ...copy[index], engine: customName, isCustomEngine: false }
        return copy
      })
    }
  }

  const handleLubeRowChange = (
    index: number,
    field: keyof LubeRow,
    value: string
  ) => {
    if (field !== 'grades' && field !== 'isCustomGrade' && value !== '' && !/^\d*\.?\d*$/.test(value)) {
      return
    }

    setLubeRows((prev) => {
      const copy = [...prev]
      const updatedRow = { ...copy[index], [field]: value }

      if (field === 'grades' && value === 'Others') {
        updatedRow.isCustomGrade = true
        updatedRow.grades = ''
      } else if (field === 'grades' && updatedRow.isCustomGrade) {
        updatedRow.grades = value
      }

      // Auto-calculate closing stock for Lube Oil
      if (field === 'openingStock' || field === 'received' || field === 'consumedByVessel') {
        const hasOp = updatedRow.openingStock !== undefined && updatedRow.openingStock !== null && updatedRow.openingStock !== ''
        const op = parseFloat(updatedRow.openingStock) || 0
        const rec = parseFloat(updatedRow.received) || 0
        const cons = parseFloat(updatedRow.consumedByVessel) || 0
        
        if (hasOp || rec > 0 || cons > 0) {
          updatedRow.closingStock = Math.max(0, op + rec - cons).toFixed(3)
        } else {
          updatedRow.closingStock = ''
        }
      }

      copy[index] = updatedRow
      return copy
    })
  }

  const addFuelRow = () => setFuelRows((prev) => [...prev, blankFuelRow()])
  const removeFuelRow = (index: number) => {
    setFuelRows((prev) => {
      const next = prev.filter((_, i) => i !== index)
      return next.length ? next : [blankFuelRow()]
    })
  }

  const addLubeRow = () => setLubeRows((prev) => [...prev, blankLubeRow()])
  const removeLubeRow = (index: number) => {
    setLubeRows((prev) => {
      const next = prev.filter((_, i) => i !== index)
      return next.length ? next : [blankLubeRow()]
    })
  }

  const emitDraft = () => {
    onSave({
      id: 0,
      reportDate,
      vesselId: 0,
      vesselName: selectedVessel,
      fuelRows,
      lubeRows,
      openingStock,
      totalConsumption: totalConsumption.toFixed(3),
      bunker,
      closingStock: closingStock || '0.000',
      lastRunningHours,
      totalRunningHours: totalRunningHours.toFixed(1),
      meCons: '',
      hydEngineCons: '',
      dredgeEngineCons: '',
      aeCons: '',
      harborDgCons: '',
      btCons: '',
      totalFoCons: totalConsumption.toFixed(3),
      dfdRemarks: remarks,
      remarks: '',
    } as any)
  }

  useEffect(() => {
    if (!selectedVessel) return
    emitDraft()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVessel, reportDate, fuelRows, lubeRows, openingStock, bunker, lastRunningHours, totalConsumption, totalRunningHours, closingStock])

  const displayValue = (val: string) => (String(val ?? '').trim() === '' ? '' : val)

  if (!selectedVessel) {
    return null
  }

  return (
    <div className='card card-flush shadow-sm mb-6'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <h3 className='card-title fw-bolder'>Add Daily Fuel Data</h3>
      </div>
      <div className='card-body py-5'>
        {/* Top Form: Vessel Name */}
        <div className='row mb-8'>
          <div className='col-md-6'>
            <label className='form-label fw-bolder text-dark'>Vessel Name</label>
            <input
              type='text'
              className='form-control form-control-solid bg-light'
              value={selectedVessel}
              readOnly
            />
          </div>
        </div>

        {/* Summary Section */}
        {(
          <div className='card card-flush shadow-sm mb-6 border'>
            <div className='card-body pt-5 pb-5'>
              <div className='row g-4'>
                <div className='col'>
                  <label className='form-label fw-bolder text-dark fs-7'>Opening Stock</label>
                  <input
                    type='text'
                    className={`form-control form-control-sm text-end ${hasPreviousReport ? 'bg-light' : ''}`}
                    placeholder='0.000'
                    value={displayValue(openingStock)}
                    onChange={(e) => {
                      if (!/^\d*\.?\d*$/.test(e.target.value)) return
                      setOpeningStock(e.target.value)
                    }}
                    readOnly={hasPreviousReport}
                  />
                  {hasPreviousReport && (
                    <small className='text-muted'>Auto-filled</small>
                  )}
                </div>
                <div className='col'>
                  <label className='form-label fw-bolder text-dark fs-7'>Total Consumption</label>
                  <input
                    type='text'
                    className='form-control form-control-sm text-end bg-light'
                    placeholder='0.000'
                    value={totalConsumption > 0 ? totalConsumption.toFixed(3) : ''}
                    readOnly
                  />
                </div>
                <div className='col'>
                  <label className='form-label fw-bolder text-dark fs-7'>Last Running Hrs</label>
                  <input
                    type='text'
                    className={`form-control form-control-sm text-end ${hasPreviousRunningHours ? 'bg-light' : ''}`}
                    placeholder='0.0'
                    value={displayValue(lastRunningHours)}
                    onChange={(e) => {
                      if (!/^\d*\.?\d*$/.test(e.target.value)) return
                      setLastRunningHours(e.target.value)
                    }}
                    readOnly={hasPreviousRunningHours}
                  />
                  {hasPreviousRunningHours && (
                    <small className='text-muted'>Auto-filled</small>
                  )}
                </div>
                <div className='col'>
                  <label className='form-label fw-bolder text-dark fs-7'>Total Running Hrs</label>
                  <input
                    type='text'
                    className='form-control form-control-sm text-end bg-light'
                    placeholder='0.0'
                    value={totalRunningHours > 0 ? totalRunningHours.toFixed(1) : ''}
                    readOnly
                  />
                </div>
                <div className='col'>
                  <label className='form-label fw-bolder text-dark fs-7'>Bunker</label>
                  <input
                    type='text'
                    className='form-control form-control-sm text-end'
                    placeholder='0.000'
                    value={displayValue(bunker)}
                    onChange={(e) => {
                      if (!/^\d*\.?\d*$/.test(e.target.value)) return
                      setBunker(e.target.value)
                    }}
                  />
                </div>
                <div className='col'>
                  <label className='form-label fw-bolder text-dark fs-7'>Closing Stock</label>
                  <input
                    type='text'
                    className='form-control form-control-sm text-end bg-light'
                    placeholder='0.000'
                    value={displayValue(closingStock)}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fuel Consumption Detail Table */}
        {(
          <div className='card card-flush shadow-sm border mb-6'>
            <div className='card-body pt-5 pb-5'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h6 className='m-0 fw-bolder'>Fuel Consumption Details</h6>
                <button className='btn btn-sm btn-primary' onClick={addFuelRow}>+ Add Row</button>
              </div>
              <div className='table-responsive'>
                <table className='table table-bordered table-sm align-middle text-center mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th style={{ width: '50px' }}>Sr</th>
                      <th style={{ width: '170px' }}>Engine</th>
                      <th>Consumption (KL)</th>
                      <th>Last Running Hours</th>
                      <th>Opening Running Hours</th>
                      <th>Closing Running Hours</th>
                      <th>Average</th>
                      <th style={{ width: '80px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fuelRows.map((row, idx) => {
                      const engineOptions = getSelectableEquipmentForRow(
                        fuelRows,
                        idx,
                        ENGINE_OPTIONS
                      )
                      return (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            {row.isCustomEngine ? (
                               <div className='d-flex'>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm me-2'
                                    value={row.engine}
                                    placeholder='Type engine name...'
                                    onChange={(e) => handleFuelRowChange(idx, 'engine', e.target.value)}
                                    onBlur={() => handleCustomEngineBlur(idx)}
                                    autoFocus
                                  />
                                  <button
                                    className='btn btn-sm btn-icon btn-light-danger'
                                    onClick={() => {
                                      setFuelRows(prev => {
                                        const c = [...prev];
                                        c[idx].isCustomEngine = false;
                                        c[idx].engine = '';
                                        return c;
                                      })
                                    }}
                                  >✕</button>
                               </div>
                            ) : (
                              <SearchableSelect
                                className='form-select form-select-sm'
                                value={row.engine}
                                onChange={(e) => handleFuelRowChange(idx, 'engine', e.target.value)}
                              >
                                <option value=''>Select Engine</option>
                                {engineOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                                <option value='Others'>Others</option>
                              </SearchableSelect>
                            )}
                          </td>
                          <td>
                            <input
                              type='text'
                              className={`form-control form-control-sm text-end ${(parseFloat(row.consumption) || 0) > getMaxConsumptionForRow(idx) ? 'border-danger' : ''}`}
                              placeholder='0.000'
                              value={displayValue(row.consumption)}
                              onChange={(e) => handleFuelRowChange(idx, 'consumption', e.target.value)}
                            />
                            {(parseFloat(row.consumption) || 0) > getMaxConsumptionForRow(idx) && (
                              <small className='text-danger d-block mt-1' style={{ fontSize: '10px' }}>
                                You can fill only {getMaxConsumptionForRow(idx).toFixed(3)} KL
                              </small>
                            )}
                          </td>
                          <td>
                            <input
                              type='text'
                              className={`form-control form-control-sm text-end ${row.lastRunningHours ? 'bg-light' : ''}`}
                              placeholder='0.000'
                              value={displayValue(row.lastRunningHours)}
                              onChange={(e) => handleFuelRowChange(idx, 'lastRunningHours', e.target.value)}
                              readOnly={Boolean(row.lastRunningHours && hasPreviousReport)}
                            />
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end'
                              placeholder='0.000'
                              value={displayValue(row.openingRunningHours)}
                              onChange={(e) => handleFuelRowChange(idx, 'openingRunningHours', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end bg-light'
                              placeholder='0.000'
                              value={displayValue(row.closingRunningHours)}
                              readOnly
                            />
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end bg-light'
                              placeholder='0.000'
                              value={displayValue(row.average)}
                              readOnly
                            />
                          </td>
                          <td>
                            <button
                              className='btn btn-sm btn-outline-danger py-1'
                              onClick={() => removeFuelRow(idx)}
                              disabled={fuelRows.length === 1}
                              title='Remove'
                            >
                              x
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Lube Oil Consumption Table */}
        {(
          <div className='card card-flush shadow-sm border mb-6'>
            <div className='card-body pt-5 pb-5'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h6 className='m-0 fw-bolder'>Lube Oil Status</h6>
                <button className='btn btn-sm btn-primary' onClick={addLubeRow}>+ Add Row</button>
              </div>
              <div className='table-responsive'>
                <table className='table table-bordered table-sm align-middle text-center mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th style={{ width: '70px' }}>Sr. No</th>
                      <th style={{ width: '300px' }}>Grades</th>
                      <th>Opening Stock</th>
                      <th>Received</th>
                      <th>Consumed by Vessel</th>
                      <th>Closing Stock</th>
                      <th style={{ width: '80px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lubeRows.map((row, idx) => {
                      const gradeOptions = getSelectableGradesForRow(lubeRows, idx, LUBE_OIL_GRADE_OPTIONS)
                      return (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            {row.isCustomGrade ? (
                               <div className='d-flex'>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm me-2'
                                    value={row.grades}
                                    placeholder='Type grade...'
                                    onChange={(e) => handleLubeRowChange(idx, 'grades', e.target.value)}
                                  />
                                  <button
                                    className='btn btn-sm btn-icon btn-light-danger'
                                    onClick={() => {
                                      setLubeRows(prev => {
                                        const c = [...prev];
                                        c[idx].isCustomGrade = false;
                                        c[idx].grades = '';
                                        return c;
                                      })
                                    }}
                                  >✕</button>
                               </div>
                            ) : (
                              <SearchableSelect
                                className='form-select form-select-sm'
                                value={row.grades}
                                onChange={(e) => handleLubeRowChange(idx, 'grades', e.target.value)}
                              >
                                <option value=''>Select Grade</option>
                                {gradeOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                                <option value='Others'>Others</option>
                              </SearchableSelect>
                            )}
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end'
                              placeholder='0.000'
                              value={displayValue(row.openingStock)}
                              onChange={(e) => handleLubeRowChange(idx, 'openingStock', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end'
                              placeholder='0.000'
                              value={displayValue(row.received)}
                              onChange={(e) => handleLubeRowChange(idx, 'received', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end'
                              placeholder='0.000'
                              value={displayValue(row.consumedByVessel)}
                              onChange={(e) => handleLubeRowChange(idx, 'consumedByVessel', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm text-end bg-light'
                              placeholder='0.000'
                              value={displayValue(row.closingStock)}
                              readOnly
                            />
                          </td>
                          <td>
                            <button
                              className='btn btn-sm btn-outline-danger py-1'
                              onClick={() => removeLubeRow(idx)}
                              disabled={lubeRows.length === 1}
                              title='Remove'
                            >
                              x
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className='card card-flush shadow-sm border mb-6'>
          <div className='card-body pt-5 pb-5'>
            <label className='form-label fw-bolder text-dark fs-7'>Remarks</label>
            <textarea
              className='form-control form-control-sm'
              rows={3}
              placeholder='Enter remarks for this report'
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
        </div>

      </div>
    </div>
  )
}

export default AddDailyFuelDataModal
